/**
 * Individual NFHS publication add-ons (one kind = one purchasable add-on).
 * Legacy stacked tiers (rulebooks / rule_casebooks / rule_case_manuals) still
 * resolve via migrate helpers so existing org/seat stamps keep working.
 */

import {
  filterOutPreviousPublications,
  normalizeSelectedSports,
  publicationMatchesSport,
  publicationSportLabel,
} from './nfhsSportFilter.js'

export const NFHS_ADDON_KINDS = {
  rulebook: {
    id: 'rulebook',
    label: 'Rule Books',
    shortLabel: 'NFHS Rule Books',
    unitPrice: 9.99,
    description: 'Digital NFHS rule books for your organization.',
  },
  casebook: {
    id: 'casebook',
    label: 'Case Books',
    shortLabel: 'NFHS Case Books',
    unitPrice: 9.99,
    description: 'Digital NFHS case books for your organization.',
  },
  manual: {
    id: 'manual',
    label: 'Manuals',
    shortLabel: 'NFHS Officials Manuals',
    unitPrice: 21.99,
    description: 'Digital NFHS officials manuals for your organization.',
  },
  handbook: {
    id: 'handbook',
    label: 'Handbooks',
    shortLabel: 'NFHS Handbooks',
    unitPrice: 9.99,
    description: 'Digital NFHS handbooks for your organization.',
  },
}

export const NFHS_ADDON_KIND_ORDER = ['rulebook', 'casebook', 'manual', 'handbook']

/** @deprecated Legacy stacked packages — still recognized on read/import. */
export const NFHS_ADDON_TIERS = {
  rulebooks: {
    id: 'rulebooks',
    label: 'Rulebooks',
    shortLabel: 'NFHS Rulebooks',
    kinds: ['rulebook'],
    unitPrice: 9.99,
    description: 'Digital NFHS rules books for your organization.',
  },
  rule_casebooks: {
    id: 'rule_casebooks',
    label: 'Rule and casebooks',
    shortLabel: 'NFHS Rule + Casebooks',
    kinds: ['rulebook', 'casebook'],
    unitPrice: 19.98,
    description: 'Digital NFHS rules books and case books for your organization.',
  },
  rule_case_manuals: {
    id: 'rule_case_manuals',
    label: 'Rulebooks, casebooks, and manuals',
    shortLabel: 'NFHS Rule + Case + Manuals',
    kinds: ['rulebook', 'casebook', 'manual'],
    unitPrice: 41.97,
    description: 'Digital NFHS rules books, case books, and officials manuals.',
  },
}

export const NFHS_ADDON_TIER_ORDER = ['rulebooks', 'rule_casebooks', 'rule_case_manuals']

/** @deprecated Prefer NFHS_ADDON_KINDS. */
export const NFHS_PRICE_PARTS = Object.fromEntries(
  NFHS_ADDON_KIND_ORDER.map((id) => {
    const kind = NFHS_ADDON_KINDS[id]
    return [id, { id, label: kind.label, price: kind.unitPrice }]
  })
)

export function normalizeNfhsAddonKind(value) {
  const key = String(value || '').trim().toLowerCase()
  if (NFHS_ADDON_KINDS[key]) return key
  // tolerate plural / marketing labels
  if (key === 'rulebooks' || key === 'rule book' || key === 'rule books') return 'rulebook'
  if (key === 'casebooks' || key === 'case book' || key === 'case books') return 'casebook'
  if (key === 'manuals' || key === 'officials manuals' || key === 'officials manual') return 'manual'
  if (key === 'handbooks' || key === 'handbook') return 'handbook'
  return null
}

export function normalizeNfhsAddonKinds(values) {
  const list = Array.isArray(values) ? values : values != null ? [values] : []
  const out = []
  const seen = new Set()
  for (const value of list) {
    const kind = normalizeNfhsAddonKind(value)
    if (!kind || seen.has(kind)) continue
    seen.add(kind)
    out.push(kind)
  }
  return out.sort(
    (a, b) => NFHS_ADDON_KIND_ORDER.indexOf(a) - NFHS_ADDON_KIND_ORDER.indexOf(b)
  )
}

export function normalizeNfhsAddonTier(value) {
  const key = String(value || '').trim()
  return NFHS_ADDON_TIERS[key] ? key : null
}

/** Expand a legacy tier (or kind id) into a sorted kinds array. */
export function kindsFromLegacyTier(tierOrKind) {
  const tier = normalizeNfhsAddonTier(tierOrKind)
  if (tier) return [...NFHS_ADDON_TIERS[tier].kinds]
  const kind = normalizeNfhsAddonKind(tierOrKind)
  return kind ? [kind] : []
}

/**
 * Resolve kinds from org / batch / cart shapes (new + legacy).
 * @param {unknown} source
 */
export function resolveNfhsAddonKinds(source) {
  if (source == null) return []
  if (typeof source === 'string') {
    return kindsFromLegacyTier(source)
  }
  if (Array.isArray(source)) {
    return normalizeNfhsAddonKinds(source)
  }
  if (typeof source === 'object') {
    if (Array.isArray(source.nfhsAddonKinds) && source.nfhsAddonKinds.length) {
      return normalizeNfhsAddonKinds(source.nfhsAddonKinds)
    }
    if (Array.isArray(source.kinds) && source.kinds.length) {
      return normalizeNfhsAddonKinds(source.kinds)
    }
    if (source.nfhsAddonTier) {
      return kindsFromLegacyTier(source.nfhsAddonTier)
    }
    if (source.nfhsAddonKind) {
      return normalizeNfhsAddonKinds([source.nfhsAddonKind])
    }
  }
  return []
}

export function unitPriceForKind(kind) {
  const normalized = normalizeNfhsAddonKind(kind)
  return normalized ? NFHS_ADDON_KINDS[normalized].unitPrice : 0
}

/** Partner / State Partner unit price (canonical catalog rate). */
export function partnerUnitPriceForKind(kind) {
  return unitPriceForKind(kind)
}

/** Round to cents. */
export function roundNfhsMoney(value) {
  return Math.round((Number(value) || 0) * 100) / 100
}

/**
 * Kept for callers that still branch on Premium seat licenses; add-ons do not use markup.
 */
export function isNfhsPremiumLicenseType(licenseType) {
  const type = String(licenseType || '').trim()
  return type === 'Premium' || type === 'Supp. Premium'
}

/** Add-ons are flat catalog pricing — no list markup. */
export function nfhsAddonListMarkupForLicense(_licenseType) {
  return 0
}

/** Add-ons do not receive State Partner discounts. */
export function nfhsAddonDiscountPercentForLicense(_licenseType) {
  return 0
}

/**
 * List unit price matches catalog / partner rate (no add-on markup).
 * @param {string} kind
 * @param {string | null | undefined} [_licenseType]
 */
export function listUnitPriceForKind(kind, _licenseType) {
  return partnerUnitPriceForKind(kind)
}

/**
 * Payable unit price is always the flat catalog rate ($9.99 / $21.99).
 * @param {string} kind
 * @param {string | null | undefined} [_licenseType]
 * @param {boolean} [_qualifiesForStatePartner]
 */
export function effectiveUnitPriceForKind(kind, _licenseType, _qualifiesForStatePartner) {
  return partnerUnitPriceForKind(kind)
}

export function unitPriceForKinds(kinds) {
  return normalizeNfhsAddonKinds(kinds).reduce((sum, kind) => sum + unitPriceForKind(kind), 0)
}

export function listUnitPriceForKinds(kinds, licenseType) {
  return roundNfhsMoney(
    normalizeNfhsAddonKinds(kinds).reduce(
      (sum, kind) => sum + listUnitPriceForKind(kind, licenseType),
      0
    )
  )
}

export function effectiveUnitPriceForKinds(kinds, licenseType, qualifiesForStatePartner) {
  return roundNfhsMoney(
    normalizeNfhsAddonKinds(kinds).reduce(
      (sum, kind) => sum + effectiveUnitPriceForKind(kind, licenseType, qualifiesForStatePartner),
      0
    )
  )
}

/** @deprecated Prefer unitPriceForKind / unitPriceForKinds. */
export function unitPriceForTier(tier) {
  const kinds = resolveNfhsAddonKinds(tier)
  if (kinds.length) return unitPriceForKinds(kinds)
  const normalized = normalizeNfhsAddonTier(tier)
  return normalized ? NFHS_ADDON_TIERS[normalized].unitPrice : 0
}

export function kindsForTier(tier) {
  return resolveNfhsAddonKinds(tier)
}

export function labelForKind(kind) {
  const normalized = normalizeNfhsAddonKind(kind)
  return normalized ? NFHS_ADDON_KINDS[normalized].label : ''
}

export function labelForKinds(kinds) {
  const list = normalizeNfhsAddonKinds(kinds)
  if (!list.length) return ''
  return list.map(labelForKind).join(', ')
}

/** @deprecated Prefer labelForKind / labelForKinds. */
export function labelForTier(tier) {
  const kinds = resolveNfhsAddonKinds(tier)
  if (kinds.length) return labelForKinds(kinds)
  const normalized = normalizeNfhsAddonTier(tier)
  return normalized ? NFHS_ADDON_TIERS[normalized].label : ''
}

export function nfhsPublicationKind(type) {
  const value = String(type || '').toLowerCase()
  if (!value) return 'other'
  if (value.includes('handbook') || value.includes('hand book')) return 'handbook'
  if (value.includes('case')) return 'casebook'
  if (value.includes('manual') || value.includes('official')) return 'manual'
  if (value.includes('rule')) return 'rulebook'
  return 'other'
}

export function kindsIncludeKind(kinds, kind) {
  const normalized = normalizeNfhsAddonKind(kind)
  if (!normalized) return false
  return resolveNfhsAddonKinds(kinds).includes(normalized)
}

/** @deprecated Prefer kindsIncludeKind. */
export function tierIncludesKind(tier, kind) {
  return kindsIncludeKind(tier, kind)
}

/** @deprecated Prefer buying the specific kind. */
export function recommendedTierForKind(kind) {
  return normalizeNfhsAddonKind(kind) || 'rulebook'
}

export function recommendedKindForPublicationType(type) {
  const kind = nfhsPublicationKind(type)
  return kind === 'other' ? 'rulebook' : kind
}

/** @deprecated Rank only applies to legacy stacked tiers. */
export function tierRank(tier) {
  const index = NFHS_ADDON_TIER_ORDER.indexOf(normalizeNfhsAddonTier(tier))
  return index < 0 ? 0 : index + 1
}

export function enrollmentNfhsAddonKinds(row) {
  const batch = row?.licenseBatch || row?.license_batch
  if (!batch && row?.nfhsAddonKinds == null && row?.nfhsAddonTier == null) return []
  return resolveNfhsAddonKinds(batch || row)
}

/** @deprecated Prefer enrollmentNfhsAddonKinds. */
export function enrollmentNfhsAddonTier(row) {
  const kinds = enrollmentNfhsAddonKinds(row)
  if (!kinds.length) return null
  // Best-effort legacy single-tier string for old UI paths
  if (kinds.length === 1 && kinds[0] === 'rulebook') return 'rulebooks'
  if (kinds.length === 2 && kinds.includes('rulebook') && kinds.includes('casebook')) {
    return 'rule_casebooks'
  }
  if (
    kinds.length === 3
    && kinds.includes('rulebook')
    && kinds.includes('casebook')
    && kinds.includes('manual')
  ) {
    return 'rule_case_manuals'
  }
  return kinds[0]
}

export function enrollmentHasNfhsAddonKind(row, kind) {
  return kindsIncludeKind(enrollmentNfhsAddonKinds(row), kind)
}

/** Seat needs any of the target kinds it does not already have. */
export function enrollmentNeedsNfhsAddonKinds(row, targetKinds) {
  const targets = normalizeNfhsAddonKinds(targetKinds)
  if (!targets.length) return false
  const owned = new Set(enrollmentNfhsAddonKinds(row))
  return targets.some((kind) => !owned.has(kind))
}

export function enrollmentMissingNfhsAddonKinds(row, targetKinds) {
  const targets = normalizeNfhsAddonKinds(targetKinds)
  const owned = new Set(enrollmentNfhsAddonKinds(row))
  return targets.filter((kind) => !owned.has(kind))
}

/** @deprecated Prefer enrollmentNeedsNfhsAddonKinds. */
export function enrollmentNeedsNfhsAddon(row, targetTier) {
  return enrollmentNeedsNfhsAddonKinds(row, resolveNfhsAddonKinds(targetTier))
}

export function unionNfhsAddonKinds(...sources) {
  const merged = []
  for (const source of sources) {
    merged.push(...resolveNfhsAddonKinds(source))
  }
  return normalizeNfhsAddonKinds(merged)
}

export function applyNfhsAddonKindsToLicenseBatch(batch, kindsOrSource) {
  const next = batch && typeof batch === 'object' ? { ...batch } : {}
  const kinds = resolveNfhsAddonKinds(kindsOrSource)
  const merged = unionNfhsAddonKinds(next, kinds)
  if (merged.length) {
    next.nfhsAddonKinds = merged
    // Keep legacy field best-effort for older readers
    const legacy = enrollmentNfhsAddonTier({ licenseBatch: { nfhsAddonKinds: merged } })
    if (legacy && NFHS_ADDON_TIERS[legacy]) next.nfhsAddonTier = legacy
    else delete next.nfhsAddonTier
  }
  return next
}

/** @deprecated Prefer applyNfhsAddonKindsToLicenseBatch. */
export function applyNfhsAddonToLicenseBatch(batch, organizationOrTier) {
  return applyNfhsAddonKindsToLicenseBatch(batch, organizationOrTier)
}

export function stripNfhsAddonKindsFromLicenseBatch(batch, kindsToRemove = null) {
  if (!batch || typeof batch !== 'object') return batch || null
  const next = { ...batch }
  const remove = kindsToRemove == null ? null : normalizeNfhsAddonKinds(kindsToRemove)
  if (!remove) {
    delete next.nfhsAddonKinds
    delete next.nfhsAddonTier
    return next
  }
  const remaining = enrollmentNfhsAddonKinds({ licenseBatch: next }).filter(
    (kind) => !remove.includes(kind)
  )
  if (!remaining.length) {
    delete next.nfhsAddonKinds
    delete next.nfhsAddonTier
  } else {
    next.nfhsAddonKinds = remaining
    const legacy = enrollmentNfhsAddonTier({ licenseBatch: { nfhsAddonKinds: remaining } })
    if (legacy && NFHS_ADDON_TIERS[legacy]) next.nfhsAddonTier = legacy
    else delete next.nfhsAddonTier
  }
  return next
}

export function stripNfhsAddonFromLicenseBatch(batch) {
  return stripNfhsAddonKindsFromLicenseBatch(batch, null)
}

export function stripNfhsAddonFromStudentCourses(students) {
  for (const student of students || []) {
    for (const course of [...(student.allUserCourses || []), ...(student.interactiveCourses || [])]) {
      const batch = course.licenseBatch || course.license_batch
      if (!batch || typeof batch !== 'object') continue
      if (!batch.nfhsAddonTier && !(Array.isArray(batch.nfhsAddonKinds) && batch.nfhsAddonKinds.length)) {
        continue
      }
      const next = stripNfhsAddonFromLicenseBatch(batch)
      course.licenseBatch = next
      if (course.license_batch) course.license_batch = next
    }
  }
}

export function formatNfhsUnitPrice(price) {
  return `$${Number(price || 0).toFixed(2)}`
}

export function pricePartsForKinds(kinds) {
  return normalizeNfhsAddonKinds(kinds)
    .map((id) => NFHS_PRICE_PARTS[id])
    .filter(Boolean)
}

/** @deprecated Prefer pricePartsForKinds. */
export function pricePartsForTier(tier) {
  return pricePartsForKinds(resolveNfhsAddonKinds(tier))
}

export function nfhsChargeDescription({
  kind,
  kinds,
  tier,
  unitPrice,
  seatCount,
  remainingSeats = 0,
  newSeats = 0,
  includedInProgress = 0,
  isFirstPurchase = false,
} = {}) {
  const resolvedKinds = normalizeNfhsAddonKinds(
    kinds?.length ? kinds : kind ? [kind] : resolveNfhsAddonKinds(tier)
  )
  const parts = pricePartsForKinds(resolvedKinds)
  const unit = formatNfhsUnitPrice(unitPrice ?? unitPriceForKinds(resolvedKinds))
  const composition = parts.length
    ? parts.length === 1
      ? `${parts[0].label} ${formatNfhsUnitPrice(parts[0].price)}`
      : `${parts.map((part) => `${part.label} ${formatNfhsUnitPrice(part.price)}`).join(' + ')} = ${unit}/seat`
    : `${unit}/seat`
  const seats = Math.max(0, Number(seatCount) || 0)
  const remaining = Math.max(0, Number(remainingSeats) || 0)
  const added = Math.max(0, Number(newSeats) || 0)
  const inProgress = Math.max(0, Number(includedInProgress) || 0)
  const chunks = []
  if (isFirstPurchase && remaining > 0) chunks.push(`${remaining} remaining`)
  if (added > 0) chunks.push(`${added} new`)
  if (inProgress > 0) chunks.push(`${inProgress} in-progress`)
  if (chunks.length > 1) return `${composition} × ${chunks.join(' + ')} (${seats} seats)`
  if (chunks.length === 1) {
    return `${composition} × ${chunks[0]} seat${seats === 1 ? '' : 's'}`
  }
  return `${composition} × ${seats} seat${seats === 1 ? '' : 's'}`
}

export function nfhsAddonInfoText(kindOrTier) {
  const kinds = resolveNfhsAddonKinds(kindOrTier)
  const kind = kinds.length === 1 ? kinds[0] : normalizeNfhsAddonKind(kindOrTier)
  if (kind && NFHS_ADDON_KINDS[kind]) {
    const unit = formatNfhsUnitPrice(unitPriceForKind(kind))
    return (
      `${NFHS_ADDON_KINDS[kind].label} ${unit} per seat. ` +
      `First purchase covers remaining unused seats plus any in-progress courses you include. ` +
      `After it is paid, new course assignments include this add-on, and you can still include earlier in-progress courses. ` +
      `Buy each publication type separately — or together in the same cart.`
    )
  }
  const parts = pricePartsForKinds(kinds)
  const unit = formatNfhsUnitPrice(unitPriceForKinds(kinds))
  const composition = parts.length
    ? `${parts.map((part) => `${part.label} ${formatNfhsUnitPrice(part.price)}`).join(' + ')} = ${unit} per seat.`
    : `${unit} per seat.`
  return `${composition} First purchase covers remaining unused seats plus any in-progress courses you include.`
}

export function nfhsAddonCatalogBlueprints() {
  return NFHS_ADDON_KIND_ORDER.map((id) => {
    const kind = NFHS_ADDON_KINDS[id]
    return {
      id: `nfhs-${id}`,
      nfhsKind: id,
      nfhsTier: id, // backward-compatible key some cards still read
      name: kind.shortLabel,
      icon: '📘',
      price: `${formatNfhsUnitPrice(kind.unitPrice)}/seat`,
      unitPrice: kind.unitPrice,
      desc: kind.description,
      infoText: nfhsAddonInfoText(id),
      includes: [kind.label],
      priceParts: pricePartsForKinds([id]),
      action: 'Add',
    }
  })
}

export function orgHasNfhsAddonKind(organization, kind) {
  return kindsIncludeKind(resolveNfhsAddonKinds(organization), kind)
}

/**
 * How many current NFHS titles of `kind` an org could assign given its sports.
 * Normal orgs: titles whose sport matches at least one org course sport.
 * Publications-only orgs: all current titles of that kind (no sport gate).
 *
 * @param {{
 *   books?: unknown[],
 *   kind?: unknown,
 *   sports?: unknown[],
 *   publicationsOnly?: boolean,
 * }} opts
 * @returns {{
 *   kind: string | null,
 *   label: string,
 *   sports: string[],
 *   titles: Array<{ id: string, title: string, sport: string }>,
 *   count: number,
 *   needsConfirm: boolean,
 * }}
 */
export function resolveAssignableNfhsTitlesForKind({
  books = [],
  kind,
  sports = [],
  publicationsOnly = false,
} = {}) {
  const normalizedKind = normalizeNfhsAddonKind(kind)
  if (!normalizedKind) {
    return {
      kind: null,
      label: '',
      sports: [],
      titles: [],
      count: 0,
      needsConfirm: false,
    }
  }

  const ofKind = filterOutPreviousPublications(books).filter(
    (book) => nfhsPublicationKind(book?.type) === normalizedKind
  )
  const sportList = normalizeSelectedSports(sports)

  let matched
  // Publications-only orgs do not rely on course sports — expose the full kind catalog.
  if (publicationsOnly) {
    matched = ofKind
  } else if (!sportList.length) {
    matched = []
  } else {
    matched = ofKind.filter((book) =>
      sportList.some((sport) => publicationMatchesSport(book, sport))
    )
  }

  const seen = new Set()
  const titles = []
  for (const book of matched) {
    const id = String(book?.id || book?._id || '').trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    titles.push({
      id,
      title: String(book?.title || book?.name || 'Untitled').trim() || 'Untitled',
      sport: publicationSportLabel(book),
    })
  }

  return {
    kind: normalizedKind,
    label: labelForKind(normalizedKind),
    sports: sportList,
    titles,
    count: titles.length,
    needsConfirm: titles.length <= 1,
  }
}

